import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Pause as PauseIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  WorkspacePremium as WorkspacePremiumIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { hostApiClient } from '../../hostApiClient';
import {
  OrganizationSubscriptionStatus,
  SubscriptionPlanSummary,
  SubscriptionStatus,
} from '../../models/Subscription';
import SubscriptionPlanCard from './SubscriptionPlanCard';
import { resolvePlanAccent } from './subscriptionPlanUi';

function statusChipColor(
  status: SubscriptionStatus,
  cancelAtPeriodEnd: boolean,
): 'success' | 'info' | 'warning' | 'error' | 'default' {
  if (cancelAtPeriodEnd) return 'warning';
  switch (status) {
    case SubscriptionStatus.ACTIVE:
      return 'success';
    case SubscriptionStatus.TRIALING:
      return 'info';
    case SubscriptionStatus.PAST_DUE:
    case SubscriptionStatus.UNPAID:
    case SubscriptionStatus.PAUSED:
      return 'warning';
    case SubscriptionStatus.CANCELED:
    case SubscriptionStatus.INCOMPLETE:
    case SubscriptionStatus.INCOMPLETE_EXPIRED:
      return 'error';
    default:
      return 'default';
  }
}

function statusGlyph(status: SubscriptionStatus | null, cancelAtPeriodEnd: boolean) {
  if (cancelAtPeriodEnd) return <ScheduleIcon sx={{ fontSize: 16 }} />;
  switch (status) {
    case SubscriptionStatus.ACTIVE:
      return <CheckCircleIcon sx={{ fontSize: 16 }} />;
    case SubscriptionStatus.TRIALING:
      return <ScheduleIcon sx={{ fontSize: 16 }} />;
    case SubscriptionStatus.PAST_DUE:
    case SubscriptionStatus.UNPAID:
      return <WarningIcon sx={{ fontSize: 16 }} />;
    case SubscriptionStatus.CANCELED:
      return <CancelIcon sx={{ fontSize: 16 }} />;
    case SubscriptionStatus.PAUSED:
      return <PauseIcon sx={{ fontSize: 16 }} />;
    default:
      return <WorkspacePremiumIcon sx={{ fontSize: 16 }} />;
  }
}

export interface CurrentSubscriptionCardProps {
  subscription: OrganizationSubscriptionStatus;
}

/**
 * Current plan rendered with the same card language as the catalogue picker.
 * Billing actions live in {@link SubscriptionManagePanel} beside this card.
 */
const CurrentSubscriptionCard: React.FC<CurrentSubscriptionCardProps> = ({ subscription }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [plan, setPlan] = useState<SubscriptionPlanSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const planCode = subscription.planCode;
  const status = subscription.status;
  const cancelAtPeriodEnd = subscription.cancelAtPeriodEnd === true;

  const loadPlan = useCallback(async () => {
    if (!planCode) {
      setPlan(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const plans = await hostApiClient.getSubscriptionPlans();
      setPlan(plans.find((entry) => entry.code === planCode) ?? null);
    } catch (err) {
      console.error('Error loading current subscription plan:', err);
      setError(t('subscription.fetchPlansError', 'Failed to fetch subscription plans'));
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [planCode, t]);

  useEffect(() => {
    void loadPlan();
  }, [loadPlan]);

  const formatDate = (value: string | null | undefined) => {
    if (!value) return null;
    return new Date(value).toLocaleDateString();
  };

  const periodEndLabel = formatDate(
    status === SubscriptionStatus.TRIALING
      ? subscription.trialEndsAt ?? subscription.currentPeriodEndsAt
      : subscription.currentPeriodEndsAt,
  );

  const statusMessage = useMemo(() => {
    if (cancelAtPeriodEnd) {
      return t(
        'subscription.cancelScheduledHint',
        'Cancellation is scheduled. You keep full access until the end date.',
      );
    }
    switch (status) {
      case SubscriptionStatus.PAST_DUE:
        return t(
          'subscription.pastDueMessage',
          'Your payment is past due. Update your payment method to keep using Band Space.',
        );
      case SubscriptionStatus.UNPAID:
        return t(
          'subscription.unpaidMessage',
          'Your payment has failed. Update your payment method or choose a new plan below.',
        );
      case SubscriptionStatus.PAUSED:
        return t(
          'subscription.pausedMessage',
          'Your subscription is currently paused. Manage billing in Stripe to reactivate it.',
        );
      case SubscriptionStatus.INCOMPLETE:
        return t(
          'subscription.incompleteMessage',
          'Your subscription setup is incomplete. Update billing or choose a new plan below to finish setup.',
        );
      case SubscriptionStatus.INCOMPLETE_EXPIRED:
        return t(
          'subscription.incompleteExpiredMessage',
          'Your subscription setup has expired. Please choose a new plan below to start a fresh subscription.',
        );
      case SubscriptionStatus.CANCELED:
        return t(
          'subscription.cancelledMessage',
          'Your subscription has been cancelled. Choose a plan below to reactivate.',
        );
      default:
        return null;
    }
  }, [cancelAtPeriodEnd, status, t]);

  if (!planCode) {
    return null;
  }

  if (loading && !plan) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !plan) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {error}
      </Alert>
    );
  }

  // Catalogue miss (rare): compact status summary without actions.
  if (!plan) {
    return (
      <Box
        sx={{
          maxWidth: 420,
          p: 3,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h5" fontWeight={800}>
              {t(`subscription.planCodes.${planCode}`, planCode.replace(/_/g, ' '))}
            </Typography>
            {status && (
              <Chip
                size="small"
                icon={statusGlyph(status, cancelAtPeriodEnd)}
                label={
                  cancelAtPeriodEnd
                    ? t('subscription.status.cancelScheduled', 'Cancel scheduled')
                    : t(`subscription.status.${status}`, status)
                }
                color={statusChipColor(status, cancelAtPeriodEnd)}
                sx={{ fontWeight: 700 }}
              />
            )}
          </Stack>
          {statusMessage && (
            <Typography variant="body2" color="text.secondary">
              {statusMessage}
            </Typography>
          )}
        </Stack>
      </Box>
    );
  }

  const accent = resolvePlanAccent(plan.code, plan.tier, theme);
  const isTrialing = status === SubscriptionStatus.TRIALING && !cancelAtPeriodEnd;

  const cornerBadges = status ? (
    <Chip
      size="small"
      icon={statusGlyph(status, cancelAtPeriodEnd)}
      label={
        cancelAtPeriodEnd
          ? t('subscription.status.cancelScheduled', 'Cancel scheduled')
          : t(`subscription.status.${status}`, status)
      }
      color={statusChipColor(status, cancelAtPeriodEnd)}
      sx={{ fontWeight: 700 }}
    />
  ) : null;

  const meta = statusMessage ? (
    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
      {statusMessage}
    </Typography>
  ) : null;

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <SubscriptionPlanCard
        plan={plan}
        accent={accent}
        isCurrent
        trialUntilDate={isTrialing ? periodEndLabel : null}
        activeTrial={isTrialing}
        actionLabel={t('subscription.currentPlan', 'Current')}
        actionDisabled
        meta={meta}
        cornerBadges={cornerBadges}
        actionSlot={null}
        fullHeight={false}
      />
    </Box>
  );
};

export default CurrentSubscriptionCard;
